import { Box, Flex, HStack, Text } from '@chakra-ui/react'
import type { IHeaderParams } from 'ag-grid-enterprise'
import { ReactNode } from 'react'

import { Icon } from '../Icons/Icon'
import { Tooltip } from '../Tooltip/Tooltip'
import { getTableHeaderSortDisplay } from './agGridSortPriority.utils'

/** Params handed to a column-menu renderer supplied through the Table pass-through. */
export type ColumnMenuRenderParams = {
  colId: string
  field?: string
  displayName: string
}

/** Pass-through used by the Table host to supply column-header menu content (e.g. the filter UI). */
export type RenderColumnMenu = (
  params: ColumnMenuRenderParams,
) => ReactNode | null

type TableFilterHeaderContext = {
  renderColumnMenu?: RenderColumnMenu
  /** Instantly autosizes a single column by its colId / field key. */
  onAutoSizeColumn?: (columnKey: string) => void
  /** Bumps when column-menu content should refresh (e.g. filter state changed). */
  columnMenuRevision?: unknown
  /** Bumps when any column sort state changes so all headers re-render. */
  sortRevision?: unknown
}

/**
 * Custom AG Grid header component.
 *
 * Replaces AG Grid's default header (and its legacy hamburger column menu) with:
 * - a sortable label that drives sorting via AG Grid's `progressSort`,
 * - an optional hover autosize action (instant apply), and
 * - a host-supplied column-menu slot (via `context.renderColumnMenu`) that the
 *   table widget uses to render the column-header filter affordance.
 *
 * Column visibility, pinning, and reset live in Manage Columns.
 * Layout order: Sort label (+ indicator) → Autosize → Filter.
 */
export const TableFilterHeader = (props: IHeaderParams) => {
  const { api, displayName, enableSorting, column, progressSort } = props
  const context = props.context as TableFilterHeaderContext | undefined
  const sortStateKey =
    context?.sortRevision != null ? String(context.sortRevision) : '0'

  const sort = column.getSort() ?? null
  const { sortOrderIndex, sortedColumnCount } = getTableHeaderSortDisplay(
    api,
    column,
  )

  const colDef = column.getColDef()
  const headerClass = colDef.headerClass
  const alignRight = Array.isArray(headerClass)
    ? headerClass.includes('ag-cell-align-right')
    : headerClass === 'ag-cell-align-right'

  const menuRevisionKey =
    context?.columnMenuRevision != null
      ? String(context.columnMenuRevision)
      : '0'
  const menuContent = context?.renderColumnMenu?.({
    colId: column.getColId(),
    field: colDef.field,
    displayName,
  })

  const columnKey = column.getColId()
  const onAutoSizeColumn = context?.onAutoSizeColumn
  const showAutosize = Boolean(onAutoSizeColumn && columnKey)

  const showSortOrder =
    sort != null && sortedColumnCount > 1 && sortOrderIndex != null

  // Announce sort state to assistive tech. AG Grid manages `aria-sort` on the
  // columnheader cell wrapper; this label makes the state explicit on the
  // interactive control itself.
  const sortStateLabel =
    sort === 'asc'
      ? ', sorted ascending'
      : sort === 'desc'
        ? ', sorted descending'
        : ''
  const sortPriorityLabel = showSortOrder
    ? `, sort priority ${sortOrderIndex + 1}`
    : ''
  const sortAriaLabel = enableSorting
    ? `Sort by ${displayName}${sortStateLabel}${sortPriorityLabel}`
    : undefined

  const onLabelClick = (event: React.MouseEvent) => {
    if (!enableSorting) return
    progressSort(event.shiftKey)
  }

  const onLabelKeyDown = (event: React.KeyboardEvent) => {
    if (!enableSorting) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      progressSort(event.shiftKey)
    }
  }

  const onAutosizeClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!onAutoSizeColumn || !columnKey) return
    onAutoSizeColumn(columnKey)
  }

  return (
    <Flex
      className="helo-header-cell"
      align="center"
      width="100%"
      minW={0}
      gap="4px"
      justifyContent="space-between"
    >
      <Flex
        align="center"
        minW={0}
        flex="1"
        justifyContent={alignRight ? 'flex-end' : 'flex-start'}
        cursor={enableSorting ? 'pointer' : 'default'}
        role={enableSorting ? 'button' : undefined}
        tabIndex={enableSorting ? 0 : undefined}
        aria-label={sortAriaLabel}
        onClick={onLabelClick}
        onKeyDown={onLabelKeyDown}
      >
        <Flex align="center" gap="12px" minW={0} maxW="100%">
          <Text
            fontSize="12px"
            fontWeight="600"
            color="presets.turquoise.100"
            minW={0}
            flex="1 1 auto"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {displayName}
          </Text>
          {sort != null && (
            <Flex
              align="flex-start"
              flexShrink={0}
              gap="2px"
              data-testid="table-sort-indicator"
              data-sort={sort}
            >
              <Icon
                icon={sort === 'asc' ? 'LongArrowUpIcon' : 'LongArrowDownIcon'}
                w="8.5px"
                h="16px"
                flexShrink={0}
                color="presets.turquoise.100"
                aria-hidden
              />
              {showSortOrder ? (
                <Text
                  key={sortStateKey}
                  fontSize="10px"
                  fontWeight="500"
                  lineHeight="1"
                  color="presets.turquoise.100"
                  flexShrink={0}
                  aria-hidden
                  data-testid="table-sort-priority"
                >
                  {sortOrderIndex + 1}
                </Text>
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>
      {showAutosize || menuContent ? (
        <HStack
          gap="2px"
          flexShrink={0}
          alignItems="center"
          onClick={event => event.stopPropagation()}
          onKeyDown={event => event.stopPropagation()}
        >
          {showAutosize ? (
            <Tooltip
              label="Autosize This Column"
              openDelay={500}
              placement="top"
            >
              <Box
                as="button"
                type="button"
                className="column-autosize-trigger"
                data-testid={`column-autosize-button-${columnKey}`}
                aria-label="Autosize This Column"
                onClick={onAutosizeClick}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                boxSize="20px"
                flexShrink={0}
                borderRadius="2px"
                cursor="pointer"
                bg="transparent"
                color="presets.turquoise.100"
                _hover={{ bg: 'presets.neutral.20' }}
                _active={{ bg: 'presets.neutral.40' }}
              >
                <Icon icon="AutosizeIcon" boxSize="12px" aria-hidden />
              </Box>
            </Tooltip>
          ) : null}
          {menuContent ? (
            <Box key={menuRevisionKey} flexShrink={0}>
              {menuContent}
            </Box>
          ) : null}
        </HStack>
      ) : null}
    </Flex>
  )
}

TableFilterHeader.displayName = 'TableFilterHeader'
